import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, Nav, NavItem, NavLink, TabContent } from 'reactstrap';
import { updateUi, clearLastTicketImportLogs, clearTicketsImportPreview, } from '../../redux';
import { ScreeningsImporter, TicketsImporter, TicketsRawImporter } from '..';
import PromoCodesImporter from '../PromoCodesImporter';
import ArticleStocksImporter from '../ArticleStocksImporter';
import ArticlesImporter from '../ArticlesImporter';
import { Setting } from '../../models';
import {
    canSyncArticleStocks,
    canSyncArticles,
    canSyncArticlesTab,
    canSyncPromoCodes,
    canSyncScreenings,
    canSyncTickets,
    screeningsSyncTabLabel,
} from '../../services';
import classnames from 'classnames';

import './SynchronisationPanel.css';

/**
 * Sliding panel for screenings sync, tickets import, promo-code import,
 * and article catalogue / stock import.
 * Tabs are gated by ability + (for screenings) instance import configuration.
 */
const SynchronisationPanel = () => {
    const { t }    = useTranslation();
    const dispatch = useDispatch();

    const [activeTab, setActiveTab]               = useState('screenings');
    const [activeTicketsTab, setActiveTicketsTab] = useState('csv');
    const [activeArticlesTab, setActiveArticlesTab] = useState('catalogue');

    const { showSynchronisationPanel } = useSelector(state => state.ui);
    const { setting, isLoading: isLoadingSetting } = useSelector(state => state.settings);
    const { user } = useSelector(state => state.auth);

    const screeningsImportConfig = useMemo(
        () => setting ? (new Setting(setting)).getScreeningsImportConfiguration() : null,
        [setting]
    );
    const ticketsImportConfig = useMemo(
        () => setting ? (new Setting(setting)).getTicketsImportConfiguration() : null,
        [setting]
    );

    const canShowScreeningsTab = !!screeningsImportConfig && canSyncScreenings(user);
    const canShowTicketsTab = canSyncTickets(user);
    const canShowPromoCodesTab = canSyncPromoCodes(user);
    const canShowArticlesTab = canSyncArticlesTab(user);
    const canShowCatalogueSubTab = canSyncArticles(user);
    const canShowStocksSubTab = canSyncArticleStocks(user);
    const settingsResolved = !!setting || !isLoadingSetting;
    // Keep the screenings tab selectable while settings load so the panel does not flicker.
    const screeningsTabPending = !settingsResolved;
    const screeningsTabLabel = screeningsSyncTabLabel(t, screeningsImportConfig);

    useEffect(() => {
        if (showSynchronisationPanel)
            setActiveTab('screenings');
    }, [showSynchronisationPanel]);

    useEffect(() => {
        if (activeArticlesTab === 'catalogue' && !canShowCatalogueSubTab && canShowStocksSubTab)
            setActiveArticlesTab('stocks');
        else if (activeArticlesTab === 'stocks' && !canShowStocksSubTab && canShowCatalogueSubTab)
            setActiveArticlesTab('catalogue');
    }, [activeArticlesTab, canShowCatalogueSubTab, canShowStocksSubTab]);

    /** Active tab coerced to the first visible one when rights/config change. */
    const resolvedActiveTab = (() => {
        if (activeTab === 'screenings' && (canShowScreeningsTab || screeningsTabPending))
            return 'screenings';
        if (activeTab === 'tickets' && canShowTicketsTab)
            return 'tickets';
        if (activeTab === 'promo_codes' && canShowPromoCodesTab)
            return 'promo_codes';
        if (activeTab === 'articles' && canShowArticlesTab)
            return 'articles';
        if (canShowScreeningsTab)
            return 'screenings';
        if (canShowTicketsTab)
            return 'tickets';
        if (canShowPromoCodesTab)
            return 'promo_codes';
        if (canShowArticlesTab)
            return 'articles';
        return 'screenings';
    })();

    const resolvedArticlesTab = (() => {
        if (activeArticlesTab === 'catalogue' && canShowCatalogueSubTab)
            return 'catalogue';
        if (activeArticlesTab === 'stocks' && canShowStocksSubTab)
            return 'stocks';
        if (canShowCatalogueSubTab)
            return 'catalogue';
        return 'stocks';
    })();

    const toggle = tab => {
        if (activeTab !== tab)
            setActiveTab(tab);
    };

    const toggleTickets = tab => {
        if (activeTicketsTab !== tab) {
            dispatch(clearLastTicketImportLogs());
            dispatch(clearTicketsImportPreview());
            setActiveTicketsTab(tab);
        }
    };

    const toggleArticles = tab => {
        if (activeArticlesTab !== tab)
            setActiveArticlesTab(tab);
    };

    const close = () => {
        dispatch(clearLastTicketImportLogs());
        dispatch(clearTicketsImportPreview());
        dispatch(updateUi({ showSynchronisationPanel: false }));
    };

    const visibleTabCount = [
        canShowScreeningsTab,
        canShowTicketsTab,
        canShowPromoCodesTab,
        canShowArticlesTab,
    ].filter(Boolean).length;
    const showTabNav = visibleTabCount > 1;
    const showArticlesSubNav = canShowCatalogueSubTab && canShowStocksSubTab;

    return (
        <div className={`SynchronisationPanel ${showSynchronisationPanel ? 'expanded d-block' : 'reduced'}`}>
            <Button color="danger" size="sm" className='float-right' onClick={close}>
                <i className='fa fa-times'></i>
            </Button>
            <h4 className='border-bottom mt-1'>
                <i className='fa fa-download'></i>&nbsp;{t('events.sync_and_import')}
            </h4>
            <br />
            {showTabNav && (
                <>
                    <Nav tabs>
                        {canShowScreeningsTab && (
                            <NavItem>
                                <NavLink
                                    className={classnames({ active: resolvedActiveTab === 'screenings' })}
                                    onClick={() => { toggle('screenings'); }}
                                >
                                    {screeningsTabLabel}
                                </NavLink>
                            </NavItem>
                        )}
                        {canShowTicketsTab && (
                            <NavItem>
                                <NavLink
                                    className={classnames({ active: resolvedActiveTab === 'tickets' })}
                                    onClick={() => { toggle('tickets'); }}
                                >
                                    {t('tickets.import.tab_title')}
                                </NavLink>
                            </NavItem>
                        )}
                        {canShowPromoCodesTab && (
                            <NavItem>
                                <NavLink
                                    className={classnames({ active: resolvedActiveTab === 'promo_codes' })}
                                    onClick={() => { toggle('promo_codes'); }}
                                >
                                    {t('articles.promo_codes_import.tab_title')}
                                </NavLink>
                            </NavItem>
                        )}
                        {canShowArticlesTab && (
                            <NavItem>
                                <NavLink
                                    className={classnames({ active: resolvedActiveTab === 'articles' })}
                                    onClick={() => { toggle('articles'); }}
                                >
                                    {t('articles.import.tab_title')}
                                </NavLink>
                            </NavItem>
                        )}
                    </Nav>
                    <br />
                </>
            )}
            <TabContent activeTab={resolvedActiveTab}>
                {canShowScreeningsTab && (
                    <ScreeningsImporter visible={ resolvedActiveTab === 'screenings' }/>
                )}

                {canShowTicketsTab && resolvedActiveTab === 'tickets' && (
                <>
                    {ticketsImportConfig ? (
                    <>
                        <Nav tabs>
                            <NavItem>
                                <NavLink className={classnames({ active: activeTicketsTab === 'csv' })} onClick={() => { toggleTickets('csv'); }}>
                                    {t('tickets.import.tab_csv')}
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink className={classnames({ active: activeTicketsTab === 'integration' })} onClick={() => { toggleTickets('integration'); }}>
                                    {t(`tickets.import.tab_${ticketsImportConfig.source}`)}
                                </NavLink>
                            </NavItem>
                        </Nav>
                        <br />
                        <TabContent activeTab={activeTicketsTab}>
                            <TicketsRawImporter visible={ showSynchronisationPanel && activeTicketsTab === 'csv' }/>
                            <TicketsImporter visible={ showSynchronisationPanel && activeTicketsTab === 'integration' }/>
                        </TabContent>
                    </>
                    ) : (
                        <TicketsRawImporter visible={ showSynchronisationPanel }/>
                    )}
                </>
                )}

                {canShowPromoCodesTab && (
                    <PromoCodesImporter visible={ resolvedActiveTab === 'promo_codes' }/>
                )}

                {canShowArticlesTab && resolvedActiveTab === 'articles' && (
                <>
                    {showArticlesSubNav && (
                        <>
                            <Nav tabs>
                                {canShowCatalogueSubTab && (
                                    <NavItem>
                                        <NavLink
                                            className={classnames({ active: resolvedArticlesTab === 'catalogue' })}
                                            onClick={() => { toggleArticles('catalogue'); }}
                                        >
                                            {t('articles.import.tab_catalogue')}
                                        </NavLink>
                                    </NavItem>
                                )}
                                {canShowStocksSubTab && (
                                    <NavItem>
                                        <NavLink
                                            className={classnames({ active: resolvedArticlesTab === 'stocks' })}
                                            onClick={() => { toggleArticles('stocks'); }}
                                        >
                                            {t('articles.stocks_import.tab_title')}
                                        </NavLink>
                                    </NavItem>
                                )}
                            </Nav>
                            <br />
                        </>
                    )}
                    {canShowCatalogueSubTab && (
                        <ArticlesImporter
                            visible={
                                showSynchronisationPanel
                                && (!showArticlesSubNav || resolvedArticlesTab === 'catalogue')
                            }
                        />
                    )}
                    {canShowStocksSubTab && (
                        <ArticleStocksImporter
                            visible={
                                showSynchronisationPanel
                                && (!showArticlesSubNav || resolvedArticlesTab === 'stocks')
                            }
                        />
                    )}
                </>
                )}
            </TabContent>
        </div>
    );
};

export default SynchronisationPanel;
